import Pane from './Pane';
import { usePaneManagement } from '../../contexts/PaneManagementContext';
import { useAllWorkflowsAndApplications, useUserProfile } from '../../hooks/queries';
import { isNonNullable } from '../../types/typeGuards';
import { PaneData } from '../../types';
import AppStack from '../AppStack/AppStack';
import { useApplicationStore, useUserIPRestrictionStore, useUserSettingsStore } from '../../Store';
import { messagingEnabled } from '../MessageProvider/MessageProvider';
import { useGetAllMessagingApps } from '../../hooks/queries';
import { useEffect } from 'react';
import { useChannelMessagingStore } from '../../Store/useChannelMessagingStore';
import useAuthToken from '../../hooks/useAuthToken';

export default function PaneManager() {
  const { openPanes, ctaPane, activePane, isPaneExpaneded, cdkuPane } =
    usePaneManagement();
  const { data } = useAllWorkflowsAndApplications();
  const userPreferenceSettings = useUserSettingsStore((state:any) => state.userSettings);
  const { data:oktaUser } = useUserProfile();
  const {data: channelMsgApps} = useGetAllMessagingApps();
  const appDomains = useChannelMessagingStore((state:any) => state.appDomains);
  const setAppDomains = useChannelMessagingStore((state:any) => state.setAppDomains);
  const authToken = useAuthToken();
  const ipRestriction = useUserIPRestrictionStore((state:any) => state.ipRestriction);
  const applications = useApplicationStore((state:any) => state.applications);

  const workflows = data.map((app: any) => {
    // adding logoutUrl to workflows from its parent application
    const workflow = app.appId
      ? {
          ...app,
          logoutUrl:
            data.find((item) => item.id === app.appId)?.logoutUrl || '',
        }
      : { ...app };
    return workflow;
  });

  const getIPRestrictionsWithLabel = (iprs: any[], wfls: any[]) => {
    const workflowList = applications?.reduce((acc: any, app: any) => {
    return acc?.concat(app?.workflows);
  }, []);
    try{
      return iprs.filter((ip) => {
        return ip.entityType === 'workflow' && ip.isRestricted;
      })
      .map((ip) => { 
        const workflow = workflowList?.find((wf:any) => wf.workflowId === ip.entityId)?.name;
        return { ...ip, workflow }
      });
    }
    catch(e){ console.warn('ERROR in getIPRestrictionsWithLabel', e) }
  }

  useEffect(() => {
    if(isPaneExpaneded && messagingEnabled(getProductId(activePane!), channelMsgApps)) {
      const { id, url: origin, name } = panes.find((pane) => pane.id === activePane);
      const activeFrm:any = document.getElementById(activePane!);
      activeFrm?.contentWindow?.postMessage(
      { 
        id,   
        action: 'UNIFY_CLIENT_CONNECT', 
        value: { 
          auth: { authToken: authToken },
          ipRestriction: getIPRestrictionsWithLabel(ipRestriction, applications),
          product:{ id, origin, name, unifyOrigin: document.location.href }, 
          user: { preferences: { language: oktaUser?.locale, ...userPreferenceSettings }}}}, 
      appDomains[id] || origin);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePane, isPaneExpaneded]);

  let panes = openPanes
    .map((paneId) =>
      (workflows || data).find((workflow: any) => workflow.id === paneId)
    )
    .filter(isNonNullable);

  const getProductId = (id: string) => {
    if(id?.substring && id?.substring(0, 1) === 'F') {
      const workflow = workflows?.find((wf) => wf.id === id);
      return workflow?.appId || id;
    }
    return id;
  }

  const frameLoadHandler = (pane: PaneData, evt: Event) => {
    const { id, url: origin, name } = pane;
    const frameWindow = (evt.target as HTMLIFrameElement).contentWindow;
    messagingEnabled(getProductId(id), channelMsgApps) && frameWindow?.postMessage(
      { 
        id, 
        action: 'UNIFY_CLIENT_CONNECT', 
        value: { 
          auth: { authToken: authToken },
          ipRestriction: getIPRestrictionsWithLabel(ipRestriction, applications),
          product:{ id, origin, name, unifyOrigin: document.location.href }, 
          user: { preferences: { language: oktaUser?.locale, ...userPreferenceSettings }}}}, 
      appDomains[id] || origin);
  };

  useEffect(() => {
    const messageHandler = (evt: MessageEvent) => {
        if(evt.data.id && evt.data.action === 'UNIFY_CLIENT_INIT_CONNECTION') {
          const { id, origin } = evt.data;
          const frameEl: any = document.getElementById(evt.data.id);
          setAppDomains({ ...appDomains, [id]: origin });

          frameEl && messagingEnabled(getProductId(id), channelMsgApps) && 
            frameEl.contentWindow?.postMessage(
              { 
                id, 
                action: 'UNIFY_CLIENT_CONNECT', 
                value: {
                  auth: { authToken: authToken },
                  ipRestriction: getIPRestrictionsWithLabel(ipRestriction, applications),
                  product:{ id, origin, unifyOrigin: document.location.href }, 
                  user: { preferences: { language: oktaUser?.locale, ...userPreferenceSettings }}}},
          appDomains[id] || origin)}
    }

    window.addEventListener('message', messageHandler);
    return () => {
      window.removeEventListener('message', messageHandler);
    };
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelMsgApps]);

  return (
    <>
      <AppStack 
        cdkuPane={cdkuPane || ''} 
        openedApps={panes} 
        activeApp={activePane || ''} 
        showStack={isPaneExpaneded}
        frameLoadHandler={frameLoadHandler} />

      {ctaPane && (
        <Pane
          key={'ctapane'}
          active={activePane === 'ctapane'}
          isExpanded={isPaneExpaneded}
          url={ctaPane}
          name={'ctapane'}
          id={'ctapane'}
        />
      )}
    </>
  );
}
